"use client";

import { useRef, useState } from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight } from "lucide-react";

export function Gallery({ photos }: { photos: { url: string; alt: string }[] }) {
  const [i, setI] = useState(0);
  const startX = useRef<number | null>(null);
  const current = photos[i] ?? photos[0];
  if (!current) return null;

  function go(delta: number) {
    setI((cur) => (cur + delta + photos.length) % photos.length);
  }

  return (
    <div className="relative z-10">
      <div
        className="relative aspect-[4/3] overflow-hidden rounded-[1.75rem] mx-4 select-none"
        onPointerDown={(e) => {
          if (e.button !== 0) return;
          startX.current = e.clientX;
        }}
        onPointerUp={(e) => {
          const origin = startX.current;
          startX.current = null;
          if (origin == null || photos.length < 2) return;
          const dx = e.clientX - origin;
          if (Math.abs(dx) > 40) go(dx > 0 ? -1 : 1);
        }}
        onPointerCancel={() => {
          startX.current = null;
        }}
      >
        <div className="absolute inset-0 pointer-events-none">
          <Image
            src={current.url}
            alt={current.alt}
            fill
            className="object-cover"
            sizes="(max-width: 640px) 100vw, 512px"
            priority
            draggable={false}
          />
        </div>
        {photos.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous photo"
              className="absolute left-2 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white"
              onClick={(e) => {
                e.stopPropagation();
                go(-1);
              }}
            >
              <ChevronLeft size={22} />
            </button>
            <button
              type="button"
              aria-label="Next photo"
              className="absolute right-2 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white"
              onClick={(e) => {
                e.stopPropagation();
                go(1);
              }}
            >
              <ChevronRight size={22} />
            </button>
            <div className="pointer-events-none absolute inset-x-0 bottom-3 z-20 flex justify-center gap-1.5">
              {photos.map((p, idx) => (
                <span
                  key={p.url}
                  className={`h-1.5 rounded-full ${idx === i ? "w-6 bg-white" : "w-1.5 bg-white/50"}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
      <div className="relative z-10 mt-3 flex gap-2 overflow-x-auto px-4 pb-1">
        {photos.map((p, idx) => (
          <button
            key={p.url}
            type="button"
            aria-label={p.alt || `Photo ${idx + 1}`}
            aria-current={idx === i}
            onClick={() => setI(idx)}
            className={`relative h-16 w-20 shrink-0 overflow-hidden rounded-2xl ${idx === i ? "ring-2 ring-clay" : "opacity-70"}`}
          >
            <span className="pointer-events-none absolute inset-0">
              <Image src={p.url} alt="" fill className="object-cover" sizes="88px" draggable={false} />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
